import ReactGA from 'react-ga4'

import { IUser } from 'core-components/sitebuilder/models/IUser'
import settings from 'src/config/settings'
import { toFormat } from 'src/core/classes/helpers/DateHelper'
import { MarketApi } from 'src/core/classes/services/api/MarketApi'
import Logger from 'src/core/utils/Logger'

import { GoogleAnalyticsEvents, GoogleAnalyticsProperties } from './types/GoogleAnalyticsTypes'

export class GoogleAnalyticsService {
  public isInit: boolean = false
  private user: IUser | null = null

  public init = (user: IUser) => {
    if (settings.googleAnalyticsId) {
      this.isInit = true

      this.user = user
      this.initialize(user)

      if (!user.id) {
        Logger.warn('GoogleAnalyticsService: user is not defined')
      } else {
        this.sendUserData()
      }
    }
  }

  public sendUserData = () => {
    const user = this.user

    if (!user?.id) {
      return
    }
    const userId = this.getUserId(user)

    if (this.isInit) {
      const dataNew = {
        p_userEncodedId: userId,
        p_country: user.country,
        p_language: user.language,
        p_hpOrg: user.hpOrg,
        p_geoMarket: user.geoMarket,
        p_region: MarketApi.getRegion(user.geoMarket).Region,
        p_dateHourMinute: toFormat(Date.now(), 'YYYYMMDDHHmm'),
        p_sessionID: this.getSessionId(),
      }

      ReactGA.gtag('set', GoogleAnalyticsProperties.userProperties, dataNew)
    }
  }

  public trackPage(location: string, title: string): boolean {
    if (this.isInit) {
      if (settings.googleAnalyticsId) {
        ReactGA.gtag('event', GoogleAnalyticsEvents.pageView, {
          page_title: title,
          page_location: location,
        })
      }
      return true
    }
    return false
  }

  public timing(timingCategory: string, timingVar: string, timingValue: number): void {
    if (this.isInit) {
      ReactGA.gtag('event', GoogleAnalyticsEvents.timingComplete, {
        event_category: 'Widget',
        name: timingVar,
        event_label: timingCategory,
        value: timingValue,
      })
    }
  }

  public trackSearch(page: string, terms: string[], categories?: string[]): boolean {
    let queryParams = ''
    if (terms.length) {
      queryParams += `?searchValue=${terms.join(' ')}`
      if (categories && categories.length) {
        const sortedCategories = categories.map((el) => el.toLowerCase()).sort()
        queryParams += `&searchCategories=${sortedCategories.join(' ')}`
      }
    }
    if (queryParams) {
      ReactGA.gtag('event', GoogleAnalyticsEvents.search, {
        params: queryParams,
      })

      return true
    }
    return false
  }

  private initialize(user: IUser) {
    const userId = this.getUserId(user)

    const gaOptions = {
      userId,
      clientId: userId,
      siteSpeedSampleRate: 100,
    }

    const gtagOptions = {
      send_page_view: false, // track page manually
    }

    ReactGA.initialize([
      {
        trackingId: settings.googleAnalyticsId,
        gaOptions,
        gtagOptions,
      },
    ])
  }

  private getUserId = (user: IUser) => {
    return user.UID || 'guest'
  }

  private getSessionId = () => {
    const sessionId = sessionStorage.getItem('__ga_session_id')

    if (!sessionId) {
      const newSessionId = Math.random().toString(36).substring(2, 15)
      sessionStorage.setItem('__ga_session_id', newSessionId)
      return newSessionId
    }

    return sessionId
  }
}

export default new GoogleAnalyticsService()
